import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import HandleQuery from '@components/HandleQuery';
import useSubscribeToJourneys from '@hooks/subscriptions/useSubscribeToJourneys';
import {
  ConsultFragment as Consult,
  ConsultUpdateDocument,
  ConsultUpdateMutation,
  ConsultUpdateMutationVariables,
  GetClientWithRolesDocument,
  GetClientWithRolesQuery,
  GetClientWithRolesQueryVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { getCareProfileName } from '@utils/modelUtils';

type FormData = {
  newJourneyId: string | null;
};

type Props = {
  clientId: string;
  consult: Consult;
  journeyId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

const ConsultMoveForm = ({ clientId, consult, journeyId, onCancel, onSuccess }: Props) => {
  const getClientQuery = useQuery<GetClientWithRolesQuery, GetClientWithRolesQueryVariables>(
    GetClientWithRolesDocument,
    {
      variables: {
        id: clientId
      }
    }
  );

  useSubscribeToJourneys(() => {
    getClientQuery.refetch();
  });

  const defaultValues: FormData = {
    newJourneyId: null
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      newJourneyId: yup.string().required(intl.formatMessage({ id: 'errors.consult.newJourneyId.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const [updateConsult, { client }] = useMutation<ConsultUpdateMutation, ConsultUpdateMutationVariables>(
    ConsultUpdateDocument,
    {
      update: () => {
        client.resetStore();
      }
    }
  );

  const onSubmit = useCallback(
    async () =>
      updateConsult({
        variables: {
          id: consult.id,
          newJourneyId: form.getValues().newJourneyId
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.consultUpdate,
          intl,
          onSuccess,
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.consult'
        });
      }),
    [consult.id, form, intl, onSuccess, updateConsult]
  );

  return (
    <HandleQuery query={getClientQuery}>
      {data => {
        const journeyOptions = data.client.roles
          .filter(role => role.journey.id !== journeyId)
          .map(role => ({
            label: getCareProfileName({ intl, journey: role.journey }),
            value: role.journey.id
          }));
        return (
          <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
            <BaseErrors errors={baseErrors} />
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.newJourneyId}
              label={intl.formatMessage({ id: 'forms.consult.newJourneyIdLabel' })}
              name="newJourneyId"
              options={journeyOptions}
            />
            <FormButtons hookForm={form} onCancel={onCancel} />
          </Form>
        );
      }}
    </HandleQuery>
  );
};

export default ConsultMoveForm;
